import type { Product, ProductVariant } from "../types.js";

interface ProductHost extends HTMLElement {
  product?: Product | null;
  selectedVariant?: ProductVariant | null;
}

interface WebflowFormState {
  form: unknown;
  btn: unknown;
  handler: unknown;
}

interface JQueryLike {
  (element: Element | never[]): unknown;
  data(element: Element, key: string): WebflowFormState | undefined;
}

const managedForms = new WeakSet<HTMLFormElement>();
const submits = 'button:not([type]), button[type="submit"], input[type="submit"]';

/** Hand ownership of a Shopify commerce form from Webflow to this runtime.
 *
 * Webflow's published forms module resets disabled before it checks
 * data-wf-no-turnstile, and its pending callbacks retain the form state.
 * Removing w-form prevents future registration; detaching the existing
 * state's DOM references prevents those old callbacks changing our controls.
 * This never changes Webflow's global handlers, site key, or other forms.
 */
export function manageCommerceForm(form: HTMLFormElement): void {
  if (managedForms.has(form)) return;
  managedForms.add(form);
  form.setAttribute("data-lmdr-commerce-form", "true");
  form.setAttribute("data-wf-no-turnstile", "");
  form.setAttribute("action", "javascript:void(0)");
  form.noValidate = true;

  const jq = (window as unknown as { jQuery?: JQueryLike }).jQuery;
  const webflowState = jq?.data(form, ".w-form");
  if (webflowState && jq) {
    // Keep the object alive for callbacks already registered by Webflow, but
    // let its reset function find no live submit buttons. No fake CAPTCHA token.
    webflowState.form = jq(document.createElement("form"));
    webflowState.btn = jq([]);
    webflowState.handler = null;
  }

  const wrapper = form.closest<HTMLElement>(".w-form");
  if (wrapper && Array.from(wrapper.querySelectorAll("form")).every((item) => item === form)) {
    wrapper.classList.remove("w-form", "w-form-loading");
    wrapper.setAttribute("data-lmdr-commerce-wrapper", "true");
  }
  form.querySelectorAll<HTMLButtonElement | HTMLInputElement>(submits).forEach((button) => {
    const webflowLoading = button.classList.contains("w-form-loading");
    button.classList.remove("w-form-loading");
    if ((webflowState || webflowLoading) && button.getAttribute("aria-busy") !== "true" &&
        button.getAttribute("data-state") !== "loading" && !button.classList.contains("is-disabled")) {
      button.disabled = false;
    }
  });
}

/** Product availability always wins over finishing an add request. */
export function syncProductFormButtons(form: HTMLFormElement): void {
  const host = form.closest<ProductHost>("smootify-product");
  if (!host) return;
  const unavailable = !host.product || !host.selectedVariant?.availableForSale;
  const busy = form.getAttribute("aria-busy") === "true";
  form.querySelectorAll<HTMLButtonElement | HTMLInputElement>(submits).forEach((button) => {
    button.disabled = busy || unavailable;
    button.classList.toggle("is-disabled", unavailable);
  });
}

/** Lock all submit buttons in the form so buy-now cannot race add-to-cart. */
export function setCommerceBusy(anchor: HTMLElement, busy: boolean): void {
  const form = anchor.closest<HTMLFormElement>("form");
  const scope = form ?? anchor;
  scope.setAttribute("data-state", busy ? "loading" : "default");
  if (busy) scope.setAttribute("aria-busy", "true");
  else scope.removeAttribute("aria-busy");
  const buttons = form ? Array.from(form.querySelectorAll<HTMLElement>(submits)) : [anchor];
  buttons.forEach((button) => {
    const active = busy && button === anchor;
    button.setAttribute("data-state", active ? "loading" : "default");
    button.querySelector<HTMLElement>('[data-state="default"]')?.classList.toggle("hidden", active);
    button.querySelector<HTMLElement>('[data-state="loading"]')?.classList.toggle("hidden", !active);
    if (busy) {
      button.setAttribute("disabled", "");
      button.setAttribute("aria-busy", "true");
    } else {
      button.removeAttribute("aria-busy");
      button.removeAttribute("disabled");
    }
  });
  if (form) syncProductFormButtons(form);
  else if (!busy) {
    const host = anchor.closest<ProductHost>("smootify-product");
    if (host?.selectedVariant && !host.selectedVariant.availableForSale) {
      anchor.setAttribute("disabled", "");
    }
  }
}

export function showCommerceError(anchor: HTMLElement, message: string | null): void {
  const scope = anchor.closest<HTMLElement>("form") ?? anchor.closest<HTMLElement>("smootify-product") ?? anchor.parentElement;
  if (!scope) return;
  let error = scope.querySelector<HTMLElement>("[data-lmdr-commerce-error]");
  if (!message) {
    if (error) { error.textContent = ""; error.hidden = true; }
    return;
  }
  if (!error) {
    error = document.createElement("div");
    error.setAttribute("data-lmdr-commerce-error", "true");
    error.setAttribute("role", "alert");
    error.style.cssText = "color:#a32622;font-size:14px;line-height:1.4;margin-top:12px";
    scope.appendChild(error);
  }
  error.textContent = message;
  error.hidden = false;
}
